// src/pages/MobiliarioProfilePage.jsx
// Ficha técnica de un elemento de mobiliario. Con `publicView` se usa para el acceso por QR.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Armchair,
  ArrowLeft,
  Building2,
  LoaderPinwheel,
  MapPin,
  Tag,
  Palette,
  ClipboardList,
  QrCode,
  Printer,
  Pencil,
  Copy,
  StickyNote,
} from "lucide-react";
import Navbar from "../components/ui/Navbar";
import MobiliarioForm from "../components/ui/MobiliarioForm";
import { InfoField, SectionCard } from "../components/ui/InfoSection";
import { hasPermission } from "../utils/permissions";
import { descargarArchivo } from "../utils/descargas";
import { ESTADO_BADGE } from "../constants/mobiliario";

const PublicTopBar = () => (
  <header className="w-full bg-white shadow-md border-b fixed top-0 z-50">
    <div className="flex items-center justify-center px-4 md:px-6 py-3">
      <img src="/logo.png" alt="Previonix Logo" className="h-14 w-auto object-contain" />
    </div>
  </header>
);

const formatFecha = (v) => (v ? new Date(v).toLocaleDateString("es-CO") : "");
const formatCosto = (v) =>
  v === null || v === undefined || v === ""
    ? ""
    : Number(v).toLocaleString("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const MobiliarioProfilePage = ({ publicView = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const cargar = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    try {
      if (publicView) {
        const res = await axios.get(`${API}/public/mobiliario/${id}`, { headers });
        // Usuario de la misma empresa → ficha completa
        if (token) {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          const empresaUsuario = user?.empresa?._id || user?.empresa;
          if (empresaUsuario && String(empresaUsuario) === String(res.data?.empresa)) {
            navigate(`/mobiliario/${id}`, { replace: true });
            return;
          }
        }
        setItem(res.data);
        return;
      }

      const [res, qrRes] = await Promise.all([
        axios.get(`${API}/mobiliario/${id}`, { headers }),
        axios.get(`${API}/mobiliario/${id}/qr`, { headers }).catch(() => null),
      ]);
      setItem(res.data);
      setQr(qrRes?.data || null);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403 && publicView && !token) {
        navigate("/login", {
          replace: true,
          state: { from: { pathname: `/open/mobiliario/${id}` } },
        });
        return;
      }
      toast.error(error?.response?.data?.message || "Error al cargar el elemento");
      if (status === 403) navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [API, id, navigate, publicView, token]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleEtiqueta = async () => {
    setPrinting(true);
    try {
      await descargarArchivo(`/mobiliario/${item._id}/etiqueta`, `etiqueta_${item.codigo}.pdf`);
    } catch {
      toast.error("Error al generar la etiqueta");
    } finally {
      setPrinting(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      await axios.put(`${API}/mobiliario/${item._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Elemento actualizado");
      setShowForm(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar el elemento");
    }
  };

  const copiarEnlace = async () => {
    try {
      await navigator.clipboard.writeText(qr.url);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const TopBar = publicView ? PublicTopBar : Navbar;

  if (loading || !item) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBar />
        <div className="pt-20 md:pt-28 pb-8 container mx-auto px-4">
          {loading ? (
            <div className="inline-flex items-center gap-2.5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
              Cargando datos, por favor espera…
            </div>
          ) : (
            <div className="inline-flex rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              No fue posible cargar la ficha del elemento.
            </div>
          )}
        </div>
      </div>
    );
  }

  const ubicacion = [item.cliente?.name, item.sede?.name, item.area?.name].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar />

      <div className="pt-20 md:pt-28 pb-8">
        <div className="container mx-auto px-4 space-y-3">
          {/* Hero */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-5">
              <div className="shrink-0 w-20 h-20 rounded-xl border border-gray-100 bg-blue-50 overflow-hidden flex items-center justify-center">
                {item.modelo?.img ? (
                  <img src={item.modelo.img} alt="Modelo" className="object-contain w-full h-full" />
                ) : (
                  <Armchair className="h-9 w-9 text-blue-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <span className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs px-2.5 py-0.5 font-medium">
                    Mobiliario
                  </span>
                  {item.estado && (
                    <span
                      className={`inline-flex items-center rounded-full text-xs px-2.5 py-0.5 font-medium ${
                        ESTADO_BADGE[item.estado] || "bg-gray-100 text-gray-500 border border-gray-200"
                      }`}
                    >
                      {item.estado}
                    </span>
                  )}
                </div>

                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  {item.tipo}
                  {item.descripcion && (
                    <span className="font-normal text-gray-500"> · {item.descripcion}</span>
                  )}
                </h1>

                <div className="flex flex-wrap gap-x-5 gap-y-0.5 mt-1 text-sm text-gray-500">
                  <span>
                    <span className="font-medium text-gray-700">Código:</span> {item.codigo}
                  </span>
                  <span>
                    <span className="font-medium text-gray-700">Inventario:</span> {item.inventario || "—"}
                  </span>
                </div>

                {ubicacion.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-sm text-gray-400">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    <span>{ubicacion.join(" · ")}</span>
                  </div>
                )}
              </div>

              {!publicView && (
                <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end shrink-0">
                  <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" /> Volver
                  </button>
                  {hasPermission("mobiliario.editar") && (
                    <button
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Ficha técnica */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 space-y-4">
              <SectionCard icon={MapPin} title="Ubicación">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <InfoField label="Cliente" value={item.cliente?.name} />
                  <InfoField label="Sede" value={item.sede?.name} />
                  <InfoField label="Área de servicio" value={item.area?.name} />
                </div>
              </SectionCard>

              <SectionCard icon={Tag} title="Identificación" iconColor="text-indigo-500">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <InfoField label="Tipo" value={item.tipo} />
                  <InfoField label="Marca" value={item.marca?.name} />
                  <InfoField label="Modelo" value={item.modelo?.name} />
                  <InfoField label="Serial" value={item.serial} />
                  <InfoField label="N° inventario" value={item.inventario} />
                  <InfoField label="Código" value={item.codigo} />
                </div>
              </SectionCard>

              <SectionCard icon={Palette} title="Características" iconColor="text-pink-500">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <InfoField label="Material" value={item.material} />
                  <InfoField label="Color" value={item.color} />
                  <InfoField label="Dimensiones" value={item.dimensiones} />
                </div>
              </SectionCard>

              <SectionCard icon={ClipboardList} title="Estado y adquisición" iconColor="text-green-500">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <InfoField label="Estado" value={item.estado} />
                  <InfoField label="Propiedad" value={item.propiedad} />
                  <InfoField label="Responsable" value={item.responsable} />
                  <InfoField label="Fecha de compra" value={formatFecha(item.fecha_compra)} />
                  {!publicView && (
                    <>
                      <InfoField label="Costo" value={formatCosto(item.costo)} />
                      <InfoField label="Proveedor" value={item.proveedor?.name} />
                    </>
                  )}
                  <InfoField label="Fecha de registro" value={formatFecha(item.createdAt)} />
                </div>
              </SectionCard>

              {item.observaciones && (
                <SectionCard icon={StickyNote} title="Observaciones" iconColor="text-amber-500">
                  <p className="text-sm text-gray-700 whitespace-pre-line">{item.observaciones}</p>
                </SectionCard>
              )}
            </div>

            {/* QR */}
            {!publicView && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 h-fit lg:sticky lg:top-28">
                <div className="flex items-center gap-2 mb-4">
                  <QrCode className="h-4 w-4 text-blue-500" />
                  <h4 className="text-sm font-semibold text-gray-700">Código QR</h4>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 flex items-center justify-center">
                  {qr?.dataUrl ? (
                    <img src={qr.dataUrl} alt={`QR ${item.codigo}`} className="w-48 h-48 object-contain" />
                  ) : (
                    <QrCode className="h-24 w-24 text-gray-200" />
                  )}
                </div>
                <p className="text-xs text-gray-400 text-center mt-3">
                  Escanéalo para abrir esta ficha desde cualquier dispositivo
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2">
                  <button
                    onClick={handleEtiqueta}
                    disabled={printing}
                    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {printing ? <LoaderPinwheel className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                    Imprimir etiqueta
                  </button>
                  {qr?.url && (
                    <button
                      onClick={copiarEnlace}
                      className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      <Copy className="h-4 w-4" /> Copiar enlace
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <MobiliarioForm item={item} onClose={() => setShowForm(false)} onSave={handleSave} />
      )}
    </div>
  );
};

export default MobiliarioProfilePage;
